import { Component, Input, OnChanges } from '@angular/core';

@Component({
    selector: 'app-static-multi-value',
    standalone: false,
    template: `
        <div class="al-static-wrap">
            @if (!values?.length) {
                <div class="al-static-value"><span class="al-static-empty">&mdash;</span></div>
            } @else {
                <div class="al-static-value" [class.is-open]="expanded">
                    @if (stacked()) {
                        <div class="al-static-list">
                            @for (item of shown(); track $index) {
                                <span>{{ item }}</span>
                            }
                        </div>
                    } @else if (expanded) {
                        <div class="al-static-cols">
                            @for (item of shown(); track $index) {
                                <span>{{ item }}</span>
                            }
                        </div>
                    } @else {
                        <span>{{ shown().join(', ') }}</span>
                    }
                </div>
                <div class="al-static-foot">
                    <span class="al-static-count">{{ shown().length }} of {{ values.length }}</span>
                    @if (values.length > visibleCount) {
                        <button type="button" class="al-static-more" (click)="expanded = !expanded">
                            {{ expanded ? 'Show less' : 'View ' + hiddenCount() + ' more' }}
                        </button>
                    }
                </div>
            }
        </div>
    `,
    styles: [`
        .al-static-wrap {
            min-height: calc(1.5em + 0.75rem + 2px);
        }

        .al-static-value {
            line-height: 1.5;
            word-break: break-word;
            color: var(--al-text);
            padding-bottom: 0.35rem;
            border-bottom: 1px dashed var(--al-border);
        }

        .al-static-value.is-open {
            max-height: 11em;
            overflow-y: auto;
            overscroll-behavior: contain;
            padding-right: 0.25rem;
        }

        .al-static-empty {
            color: var(--al-text-muted);
        }

        .al-static-cols {
            columns: 2;
            column-gap: 1.25rem;
        }

        .al-static-cols span {
            display: block;
            break-inside: avoid;
        }

        .al-static-list span {
            display: block;
            padding-left: 0.7rem;
            text-indent: -0.7rem;
            margin-bottom: 0.15rem;
        }

        .al-static-list span::before {
            content: '\\00B7';
            color: var(--al-text-muted);
            margin-right: 0.4rem;
        }

        .al-static-foot {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 0.75rem;
            margin-top: 0.35rem;
        }

        .al-static-count {
            font-size: 0.72rem;
            color: var(--al-text-muted);
            font-variant-numeric: tabular-nums;
        }

        .al-static-more {
            padding: 0;
            border: 0;
            border-bottom: 1px solid currentColor;
            background: none;
            font: inherit;
            font-size: 0.82rem;
            font-weight: 600;
            line-height: 1.3;
            color: var(--al-primary);
            cursor: pointer;
        }

        .al-static-more:focus-visible {
            outline: none;
            box-shadow: var(--al-focus);
        }
    `]
})
export class StaticMultiValueComponent implements OnChanges {
    @Input() values: string[] = [];
    @Input() visibleCount = 6;
    @Input() resetKey: string | number | null | undefined;

    expanded = false;

    private lastValuesKey = '';

    ngOnChanges(): void {
        const key = JSON.stringify([this.resetKey, this.values || []]);
        if (key !== this.lastValuesKey) {
            this.lastValuesKey = key;
            this.expanded = false;
        }
    }

    shown(): string[] {
        return this.expanded ? this.values : this.values.slice(0, this.visibleCount);
    }

    hiddenCount(): number {
        return Math.max(0, this.values.length - this.visibleCount);
    }

    stacked(): boolean {
        if (!this.values?.length) {
            return false;
        }
        const total = this.values.reduce((sum: number, value: string) => sum + (value || '').length, 0);
        return total / this.values.length > 12;
    }
}
